"use client";

import React, { useEffect, useState } from "react";
import { Shield, Lock, FileText, Mail, ChevronRight, Check, Sparkles, KeyRound } from "lucide-react";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Link } from "@/components/common/Link";
import { scrollToElement } from "@/lib/scroll";
interface PolicySection {
  id: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  text: string;
  email?: string;
  badge: string;
}
const SECTIONS: PolicySection[] = [{
  id: "zero-tracking",
  badge: "01 / TELEMETRY",
  icon: Shield,
  title: "Zero Behavioral Tracking",
  text: "We do not utilize third-party commercial advertising trackers, cross-site behavioral cookies, or invasive telemetry scripts. Your journey through the Multiverse Atlas, Quantum Lab, and digital tools leaves no commercial digital footprint."
}, {
  id: "data-storage",
  badge: "02 / SUBMISSIONS",
  icon: Lock,
  title: "Form Submissions & General Inquiries",
  text: "Information submitted through our contact channels is utilized exclusively to respond to your inquiry. We never sell, lease, or distribute visitor correspondence to external marketing entities."
}, {
  id: "retention-rights",
  badge: "03 / RIGHTS",
  icon: FileText,
  title: "Data Retention Rights",
  text: "You possess the fundamental right to request the inspection, correction, or immediate deletion of any submitted email address, contact message, or historical order record. Direct data inquiries to support@quantum-liaison.com",
  email: <>{"support@quantum-liaison.com"}<br /></>
}, {
  id: "free-reports",
  badge: "04 / FREE REPORTS",
  icon: Sparkles,
  title: "Free Dossiers & Browser-Based Generators",
  text: "All generative experiences on Quantum Liaison—including The Multiverse Life Dossier, Your Future Self, and The Cosmic Destiny Code—are completely free to explore and generate. Nothing is sold, and no credit card, billing, or payment information is ever collected. All calculations and questionnaires run client-side inside your own web browser. Your answers, drafts, and generated narratives stay on your own device and can be cleared at any time. When you export a dossier, the PDF file is compiled locally in your browser and saved directly to your device; files are never uploaded to our servers or distributed via email. Older orders made before these tools became free may still be retained in server database records solely for archival verification, and can be permanently deleted upon request by contacting us."
}, {
  id: "digital-immortality-privacy",
  badge: "05 / ARCHIVES",
  icon: KeyRound,
  title: "Digital Immortality & Vault Privacy",
  text: "Digital Immortality is accessible through a complimentary account registration. Vault entries and archive records are encrypted directly within your browser prior to storage, meaning the site and server operators never hold or see your personal vault passphrase. You retain full control over your stored records, and complete account deletion can be initiated at any time directly from the account dashboard tab."
}];
export default function PrivacyPolicy() {
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 160;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(SECTIONS[i].id);
        if (el && el.offsetTop <= scrollPosition) {
          setActiveId(SECTIONS[i].id);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="privacy-policy" data-nav="dark" className="relative text-foreground py-12 md:py-20 lg:py-24 w-full max-w-full">
      <div className="relative mx-auto max-w-6xl w-full px-3.5 sm:px-6 lg:px-8">
        {/* Compact Hero */}
        <SectionHeading as="h1" eyebrow="LEGAL DISCLOSURE • REVISION 2.4.0" title="Privacy Policy" description="Quantum Liaison adheres to a privacy-first operational standard. We believe your exploration of cosmological ideas should remain completely private and unmonitored." align="center" className="mb-8 sm:mb-16" />

        {/* Monolith layout with sticky desktop Table of Contents */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 items-start w-full">
          {/* Sticky Table of Contents (Desktop sidebar / mobile navigation chip bar) */}
          <aside className="w-full min-w-0 lg:col-span-4 lg:sticky lg:top-28">
            <div className="ql-glass p-4 sm:p-6 min-w-0 break-words">
              <div className="flex items-center justify-between pb-3 border-b border-border/60 mb-3 sm:mb-4">
                <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold flex items-center gap-2">
                  <span className="size-1.5 rotate-45 bg-primary" aria-hidden="true" />
                  Table of Contents
                </span>
                <span className="font-mono text-[11px] text-muted-foreground">{SECTIONS.length} Sections</span>
              </div>

              {/* Mobile quick-jump buttons (hidden on lg) */}
              <nav aria-label="Privacy sections (mobile)" className="flex lg:hidden flex-wrap gap-2 mb-2 w-full">
                {SECTIONS.map((sec, idx) => {
                const isActive = activeId === sec.id;
                return <Link key={sec.id} to={`#${sec.id}`} onClick={e => {
                  e.preventDefault();
                  scrollToElement(sec.id);
                }} className={`ql-chip min-h-[44px] ${isActive ? "data-[active=true]" : ""}`} data-active={isActive ? "true" : "false"}>
                      <span className="text-[10px] opacity-75">0{idx + 1}</span>
                      <span className="break-words">{sec.title}</span>
                    </Link>;
              })}
              </nav>

              {/* Desktop links list */}
              <nav aria-label="Privacy sections (desktop)" className="hidden lg:flex flex-col gap-2">
                {SECTIONS.map((sec, idx) => {
                const isActive = activeId === sec.id;
                return <Link key={sec.id} to={`#${sec.id}`} onClick={e => {
                  e.preventDefault();
                  scrollToElement(sec.id);
                }} className={`group flex items-center justify-between p-3 rounded-lg border text-left transition-all ${isActive ? "border-primary/60 bg-primary/10 text-foreground shadow-[0_0_15px_-3px_var(--glow-primary)]" : "border-transparent hover:border-border hover:bg-card/60 text-muted-foreground hover:text-foreground"}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`font-mono text-xs px-2 py-0.5 rounded border transition-colors ${isActive ? "border-primary/60 bg-primary/20 text-primary font-semibold" : "border-border/60 bg-muted/40 text-muted-foreground group-hover:text-foreground"}`}>
                          0{idx + 1}
                        </span>
                        <span className="text-sm font-medium truncate font-sans">
                          {sec.title}
                        </span>
                      </div>
                      <ChevronRight className={`size-4 shrink-0 transition-transform ${isActive ? "text-primary translate-x-0.5" : "text-muted-foreground/40 group-hover:text-foreground group-hover:translate-x-0.5"}`} />
                    </Link>;
              })}
              </nav>

              <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-border/50 text-[11px] font-mono text-muted-foreground flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span>TELEMETRY STATUS</span>
                  <span className="text-primary flex items-center gap-1 font-semibold">
                    <Check className="size-3" /> NO TRACKERS
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>ENCRYPTION</span>
                  <span className="text-foreground">TLS 1.3 MANDATED</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Reading Column: ql-glass document panel (~72ch readable width) */}
          <div className="w-full min-w-0 lg:col-span-8 flex flex-col gap-6">
            <div className="ql-glass p-5 sm:p-10 md:p-12 min-w-0 break-words">
              {/* Document metadata banner */}
              <div className="flex flex-wrap items-center justify-between gap-3 pb-5 sm:pb-6 border-b border-border/60 mb-6 sm:mb-8 font-mono text-xs">
                <div className="flex items-center gap-2 text-primary font-medium">
                  <span className="size-2 rounded-full bg-primary animate-pulse shrink-0" />
                  <span className="break-words">PLATFORM PRIVACY SPECIFICATION</span>
                </div>
                <span className="text-muted-foreground">REVISION • 2.4.0</span>
              </div>

              {/* Policy Articles */}
              <div className="flex flex-col gap-6 sm:gap-10">
                {SECTIONS.map((item, index) => {
                const IconComponent = item.icon;
                const isActive = activeId === item.id;
                return <article key={item.id} id={item.id} className={`scroll-mt-28 transition-colors p-4 sm:p-6 rounded-xl border min-w-0 break-words ${isActive ? "border-primary/40 bg-card/80 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.5)]" : "border-border/60 bg-card/40"}`}>
                      {/* Section header with numbered glowing marker */}
                      <div className="flex items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex size-9 sm:size-10 shrink-0 items-center justify-center rounded-lg border border-primary/40 bg-primary/10 text-primary shadow-[0_0_12px_-2px_var(--glow-primary)]">
                            <IconComponent className="size-4 sm:size-5" />
                          </div>
                          <div className="min-w-0">
                            <span className="block font-mono text-[11px] font-semibold text-primary tracking-widest uppercase">
                              {item.badge}
                            </span>
                            <h2 className="font-serif text-base sm:text-xl font-bold text-foreground tracking-tight break-words">
                              {index + 1}. {item.title}
                            </h2>
                          </div>
                        </div>

                        <span className="font-mono text-[10px] sm:text-[11px] px-2 py-0.5 rounded border border-border/60 bg-muted/40 text-muted-foreground self-start sm:self-auto shrink-0">
                          SEC-{index + 1}00
                        </span>
                      </div>

                      {/* Section Content */}
                      <div className="text-xs sm:text-base text-muted-foreground leading-relaxed pl-0 sm:pl-13 break-words overflow-x-auto">
                        <p className="break-words">{item.text}</p>

                        {item.email && <div className="mt-4 pt-4 border-t border-border/40 flex items-center flex-wrap gap-2">
                            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                              Data Controller Contact:
                            </span>
                            <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-primary hover:underline hover:text-primary/90 transition-colors break-all">
                              <Mail className="size-3.5 sm:size-4 shrink-0" />
                              <span>{item.email}</span>
                            </a>
                          </div>}
                      </div>
                    </article>;
              })}
              </div>

              {/* Document footer acknowledgment */}
              <div className="mt-8 sm:mt-10 pt-5 sm:pt-6 border-t border-border/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs font-mono text-muted-foreground break-words">
                <span>QUANTUM LIAISON REPOSITORY • POLICIES CURRENT</span>
                <span className="text-foreground/80">AUTHENTICATED DIGITAL ARCHIVE</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}